import {
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  BarChart3Icon,
  ExternalLinkIcon,
  GiftIcon,
  LayersIcon,
  SettingsIcon,
  Share2Icon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useAddFormFieldMutation,
  useFormDetailQuery,
  useFormResponsesQuery,
  usePatchFormMutation,
  usePatchFormStatusMutation,
  useRemoveFormFieldMutation,
  useReorderFormFieldsMutation,
} from "@/modules/forms/presentation/api/queries";
import type {
  FormDTO,
  FormFieldDTO,
} from "@/modules/forms/presentation/api/types";
import { AdminAddResponseDialog } from "@/modules/forms/presentation/ui/builder/AdminAddResponseDialog";
import { FieldEditDialog } from "@/modules/forms/presentation/ui/builder/FieldEditDialog";
import { FormBuilder } from "@/modules/forms/presentation/ui/builder/FormBuilder";
import { defaultCreateFieldInput } from "@/modules/forms/presentation/ui/builder/form-field-defaults";
import { FormEditPublishShareSection } from "@/modules/forms/presentation/ui/builder/FormEditPublishShareSection";
import { FormEditSettingsSection } from "@/modules/forms/presentation/ui/builder/FormEditSettingsSection";
import { FormLotterySection } from "@/modules/forms/presentation/ui/builder/FormLotterySection";
import { FormStatusBadge } from "@/modules/forms/presentation/ui/FormStatusBadge";
import type {
  FormFieldType,
  FormSettings,
} from "@/server/platform/db/schema/forms";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";

type FormEditTab = "builder" | "settings" | "lottery" | "publish";

function isoToDatetimeLocal(iso: string | undefined): string {
  if (!iso?.trim()) return "";
  const d = new Date(iso.trim());
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function buildMergedSettings(
  prev: FormSettings | null | undefined,
  deadlineLocal: string,
  maxResponsesStr: string,
  posterKeyLocal: string,
): FormSettings {
  const merged: FormSettings = { ...(prev ?? {}) };
  const dl = deadlineLocal.trim();
  if (dl) merged.deadline = new Date(dl).toISOString();
  else delete merged.deadline;
  const mrTrim = maxResponsesStr.trim();
  if (mrTrim) {
    const n = Number.parseInt(mrTrim, 10);
    if (Number.isFinite(n) && n > 0) merged.maxResponses = n;
    else delete merged.maxResponses;
  } else delete merged.maxResponses;
  const pk = posterKeyLocal.trim();
  if (pk) merged.posterKey = pk;
  else delete merged.posterKey;
  return merged;
}

type FormEditorBodyProps = {
  formId: string;
  form: FormDTO;
  fields: FormFieldDTO[];
  hasResponses: boolean;
  responseCount: number;
  origin: string;
  posterReadUrl: string | null;
};

function FormEditorBody({
  formId,
  form,
  fields,
  hasResponses,
  responseCount,
  origin,
  posterReadUrl,
}: FormEditorBodyProps) {
  const patchForm = usePatchFormMutation(formId);
  const patchStatus = usePatchFormStatusMutation(formId);
  const addField = useAddFormFieldMutation(formId);
  const removeField = useRemoveFormFieldMutation(formId);
  const reorderFields = useReorderFormFieldsMutation(formId);

  const canUpdate = useActionPermission("forms:update");

  const [activeTab, setActiveTab] = useState<FormEditTab>("builder");
  const [title, setTitle] = useState(() => form.title);
  const [description, setDescription] = useState(() => form.description ?? "");
  const [categoryId, setCategoryId] = useState<string | null>(
    () => form.categoryId ?? null,
  );
  const [deadlineLocal, setDeadlineLocal] = useState(() =>
    isoToDatetimeLocal(form.settings?.deadline),
  );
  const [maxResponses, setMaxResponses] = useState(() => {
    const mr = form.settings?.maxResponses;
    return mr != null && Number.isFinite(mr) ? String(mr) : "";
  });
  const [posterKey, setPosterKey] = useState(
    () => form.settings?.posterKey ?? "",
  );
  const [fieldDialog, setFieldDialog] = useState<FormFieldDTO | null>(null);
  const [addResponseOpen, setAddResponseOpen] = useState(false);
  const [viewerPin, setViewerPin] = useState("");
  const [viewerPinConfirm, setViewerPinConfirm] = useState("");
  const [viewerLinkExpiryEnabled, setViewerLinkExpiryEnabled] = useState(
    () => Boolean(form.settings?.viewerAccessExpiresAt),
  );
  const [viewerLinkExpiryLocal, setViewerLinkExpiryLocal] = useState(() =>
    isoToDatetimeLocal(form.settings?.viewerAccessExpiresAt),
  );

  useEffect(() => {
    setViewerLinkExpiryEnabled(Boolean(form.settings?.viewerAccessExpiresAt));
    setViewerLinkExpiryLocal(
      isoToDatetimeLocal(form.settings?.viewerAccessExpiresAt),
    );
  }, [form.settings?.viewerAccessExpiresAt, form.updatedAt]);

  useEffect(() => {
    setPosterKey(form.settings?.posterKey ?? "");
  }, [form.settings?.posterKey, form.updatedAt]);

  useEffect(() => {
    setCategoryId(form.categoryId ?? null);
  }, [form.categoryId, form.updatedAt]);

  const questionCount = useMemo(
    () => fields.filter((f) => f.type !== "section").length,
    [fields],
  );

  function saveMeta() {
    const t = title.trim();
    if (!t) {
      toast.error("ປ້ອນຫົວຂໍ້");
      return;
    }
    const merged = buildMergedSettings(
      form.settings,
      deadlineLocal,
      maxResponses,
      posterKey,
    );
    patchForm.mutate({
      title: t,
      description: description.trim().length ? description.trim() : null,
      categoryId,
      settings: merged,
    });
  }

  function persistMetaWithPosterKey(nextPosterKey: string) {
    const t = title.trim();
    if (!t) {
      toast.error("ປ້ອນຫົວຂໍ້");
      return;
    }
    const merged = buildMergedSettings(
      form.settings,
      deadlineLocal,
      maxResponses,
      nextPosterKey,
    );
    patchForm.mutate({
      title: t,
      description: description.trim().length ? description.trim() : null,
      categoryId,
      settings: merged,
    });
  }

  const publicHref = origin && formId ? `${origin}/f/${formId}` : "";
  const viewerViewHref = origin && formId ? `${origin}/f/${formId}/view` : "";

  function saveViewerLinkExpiry() {
    const merged: FormSettings = { ...(form.settings ?? {}) };
    if (viewerLinkExpiryEnabled) {
      const local = viewerLinkExpiryLocal.trim();
      if (!local) {
        toast.error("ເລືອກວັນເວລາຫມົດອາຍຸລິ້ງ");
        return;
      }
      const iso = new Date(local).toISOString();
      if (Number.isNaN(Date.parse(iso))) {
        toast.error("ວັນເວລາບໍ່ຖືກຕ້ອງ");
        return;
      }
      merged.viewerAccessExpiresAt = iso;
    } else {
      delete merged.viewerAccessExpiresAt;
    }
    patchForm.mutate(
      { settings: merged },
      { onSuccess: () => toast.success("ບັນທຶກການຕັ້ງຄ່າລິ້ງແລ້ວ") },
    );
  }

  function saveViewerPin() {
    if (viewerPin.length !== 6) {
      toast.error("PIN ຕ້ອງເປັນຕົວເລກ 6 ຫຼັກ");
      return;
    }
    if (viewerPin !== viewerPinConfirm) {
      toast.error("PIN ກັບຢືນຢັນບໍ່ກົງກັນ");
      return;
    }
    patchForm.mutate(
      { viewerPin },
      {
        onSuccess: () => {
          setViewerPin("");
          setViewerPinConfirm("");
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <FormStatusBadge status={form.status} />
        <Badge variant="outline">{questionCount} ຄຳຖາມ</Badge>
        <Badge variant="outline">{responseCount} ຄຳຕອບ</Badge>
        {form.viewerPinConfigured ? (
          <Badge variant="secondary">PIN ເບິ່ງຜົນ</Badge>
        ) : null}
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as FormEditTab)}
        className="w-full"
      >
        <div className="overflow-x-auto">
          <TabsList className="h-auto w-full min-w-max border-b bg-transparent p-0">
            <TabsTrigger
              value="builder"
              className="gap-2 whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
            >
              <LayersIcon className="h-4 w-4" />
              ຄຳຖາມ
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="gap-2 whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
            >
              <SettingsIcon className="h-4 w-4" />
              ຕັ້ງຄ່າ
            </TabsTrigger>
            <TabsTrigger
              value="lottery"
              className="gap-2 whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
            >
              <GiftIcon className="h-4 w-4" />
              ລຸ້ນລາງວັນ
            </TabsTrigger>
            <TabsTrigger
              value="publish"
              className="gap-2 whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6"
            >
              <Share2Icon className="h-4 w-4" />
              ເຜີຍແຜ່ ແລະ ແຊຣ
            </TabsTrigger>
          </TabsList>
        </div>

        {activeTab === "builder" ? (
          <div className="mt-6">
            <FormBuilder
              fields={fields}
              formStatus={form.status}
              canEditFields={canUpdate && form.status !== "closed"}
              canAddResponse={canUpdate}
              onAddResponse={() => setAddResponseOpen(true)}
              onReorder={(ids) => reorderFields.mutate(ids)}
              onAddField={(t: FormFieldType) =>
                addField.mutate(defaultCreateFieldInput(t))
              }
              onEditField={(f) => setFieldDialog(f)}
              onRemoveField={(fid) => removeField.mutate(fid)}
              isReorderPending={reorderFields.isPending}
            />
          </div>
        ) : null}

        {activeTab === "settings" ? (
          <div className="mt-6">
            <FormEditSettingsSection
              formId={formId}
              canUpdate={canUpdate}
              isSaving={patchForm.isPending}
              title={title}
              description={description}
              categoryId={categoryId}
              deadlineLocal={deadlineLocal}
              maxResponses={maxResponses}
              posterKey={posterKey}
              posterReadUrl={posterReadUrl}
              onTitleChange={setTitle}
              onDescriptionChange={setDescription}
              onCategoryChange={setCategoryId}
              onDeadlineChange={setDeadlineLocal}
              onMaxResponsesChange={setMaxResponses}
              onSave={saveMeta}
              onPosterUploaded={(key) => {
                setPosterKey(key);
                persistMetaWithPosterKey(key);
              }}
              onPosterRemove={() => {
                setPosterKey("");
                persistMetaWithPosterKey("");
              }}
            />
          </div>
        ) : null}

        {activeTab === "lottery" ? (
          <div className="mt-6">
            <FormLotterySection
              formId={formId}
              fields={fields}
              canUpdate={canUpdate}
            />
          </div>
        ) : null}

        {activeTab === "publish" ? (
          <div className="mt-6">
            <FormEditPublishShareSection
              form={form}
              canUpdate={canUpdate}
              hasResponses={hasResponses}
              isSaving={patchForm.isPending}
              isStatusPending={patchStatus.isPending}
              publicHref={publicHref}
              viewerViewHref={viewerViewHref}
              viewerPin={viewerPin}
              viewerPinConfirm={viewerPinConfirm}
              viewerLinkExpiryEnabled={viewerLinkExpiryEnabled}
              viewerLinkExpiryLocal={viewerLinkExpiryLocal}
              onViewerPinChange={setViewerPin}
              onViewerPinConfirmChange={setViewerPinConfirm}
              onViewerLinkExpiryEnabledChange={setViewerLinkExpiryEnabled}
              onViewerLinkExpiryLocalChange={setViewerLinkExpiryLocal}
              onPatchStatus={(status) => patchStatus.mutate({ status })}
              onSaveViewerLinkExpiry={saveViewerLinkExpiry}
              onSaveViewerPin={saveViewerPin}
            />
          </div>
        ) : null}
      </Tabs>

      <AdminAddResponseDialog
        formId={formId}
        formTitle={form.title}
        fields={fields}
        open={addResponseOpen}
        onOpenChange={setAddResponseOpen}
      />

      <FieldEditDialog
        formId={formId}
        field={fieldDialog}
        open={fieldDialog !== null}
        onOpenChange={(open) => {
          if (!open) setFieldDialog(null);
        }}
        hasResponses={hasResponses}
      />
    </div>
  );
}

export function FormEditPage() {
  const { id } = useParams({ from: "/app/forms/$id/edit" });
  const nav = useNavigate({ from: "/app/forms/$id/edit" });

  const [origin, setOrigin] = useState("");
  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  const detail = useFormDetailQuery(id);
  const form = detail.data?.form;
  const fields = detail.data?.fields ?? [];

  const respProbe = useFormResponsesQuery(id, { limit: 1, offset: 0 });
  const responseCount = respProbe.data?.meta.total ?? 0;
  const hasResponses = responseCount > 0;

  const publicHref =
    origin && form ? `${origin}/f/${form.id}` : "";

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຟອມ", to: "/app/forms/dashboard" },
            { label: "ຟອມ", to: "/app/forms" },
            form ? { label: form.title } : { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate font-semibold text-2xl tracking-tight">
                {form?.title ?? "ແກ້ໄຂຟອມ"}
              </h1>
              {form ? <FormStatusBadge status={form.status} /> : null}
            </div>
            <p className="text-muted-foreground text-sm">
              ສ້າງຄຳຖາມ · ຕັ້ງຄ່າ · ເຜີຍແຜ່ ແລະ ແຊຣຜົນຕອບ
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {form?.status === "published" && publicHref ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  window.open(publicHref, "_blank", "noopener,noreferrer")
                }
              >
                <ExternalLinkIcon className="mr-2 h-4 w-4" />
                ເປີດ public
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() =>
                nav({ to: "/app/forms/$id/responses", params: { id } })
              }
            >
              <BarChart3Icon className="mr-2 h-4 w-4" />
              ຜົນຕອບ
            </Button>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => nav({ to: "/app/forms" })}
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              ກັບລາຍການ
            </Button>
          </div>
        </div>

        <QueryState
          result={detail}
          title="ກໍາລັງໂຫຼດຟອມ"
          description="ກຳລັງດຶງຂໍ້ມູນ"
          variant="fullscreen"
        >
          {!form ? null : (
            <FormEditorBody
              key={`${form.id}-${form.updatedAt}`}
              formId={id}
              form={form}
              fields={fields}
              hasResponses={hasResponses}
              responseCount={responseCount}
              origin={origin}
              posterReadUrl={detail.data?.posterReadUrl ?? null}
            />
          )}
        </QueryState>
      </Main>
    </>
  );
}
